package de.oevbs.kunde.kundenportal2.services.mydata

import de.oevbs.businesspartner.bpcrmservice2.partner.model.*
import de.oevbs.kosmos.ui.elements.button.btn
import de.oevbs.kosmos.ui.forms.inputgroup.inputDate
import de.oevbs.kosmos.ui.forms.inputgroup.inputText
import de.oevbs.kosmos.ui.forms.radiogroup.radioGroup
import de.oevbs.kosmos.ui.forms.selectmenu.selectMenu
import de.oevbs.kosmos.ui.icons.MaterialIconsOutlined
import de.oevbs.kosmos.ui.layouts.card
import de.oevbs.kunde.kundenportal2.ContactInformationStep
import de.oevbs.kunde.kundenportal2.Formats
import de.oevbs.kunde.kundenportal2.Pages
import de.oevbs.kunde.kundenportal2.PortalContactNotes
import de.oevbs.kunde.kundenportal2.components.*
import de.oevbs.kunde.kundenportal2.model.services.mydata.*
import de.oevbs.kunde.kundenportal2.navigation.Page
import de.oevbs.kunde.kundenportal2.navigation.visit
import de.oevbs.kunde.kundenportal2.util.hyphen
import de.oevbs.sfa.data.time.toLocalizedString
import dev.fritz2.core.RenderContext
import dev.fritz2.core.invoke
import dev.fritz2.core.lensOf
import dev.fritz2.core.storeOf
import dev.fritz2.headless.validation.Severity
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.mapNotNull
import kotlinx.datetime.LocalDate

fun RenderContext.contactInformationStart(page: Page) {
    val store = ContactInformationStore(job)
    headerImageLayout {
        title(page.linkTitle)
        content {
            contentContainer {
                store.initPartnerData()
                store.currentStep.render { step ->
                    when (step) {
                        ContactInformationStep.CONTACTINFO1 -> contactInformation(store)
                        ContactInformationStep.ADDRESS2 -> addressCheck(store)
                        ContactInformationStep.SUCCESS3 -> successMessage(store)
                    }
                }
            }
        }
    }
}

private fun RenderContext.contactInformation(store: ContactInformationStore) {
    globalInitTracker.data.combine(store.initFailedFlow, ::Pair).render { (loading, failed) ->
        if (loading) return@render
        when {
            failed -> noPartnerDataFoundMessage()
            else -> contactInformationForm(store)
        }
    }
}

private fun RenderContext.contactInformationForm(store: ContactInformationStore) {
    paragraph {
        +"Hier können Sie Ihre persönlichen Daten sowie Kontaktinformationen einsehen und ändern. Ihren bevorzugten "
        textualButton("Versandweg für Vertragsdokumente") handledBy {
            Pages.inboxDelivery.visit()
        }
        +" können Sie im Postfach ändern. Ihre Zustimmung zur Kontaktaufnahme erteilen Sie in der "
        textualButton("Einwilligungserklärung") handledBy {
            Pages.myDataConsent.visit()
        }
        +"."
    }

    div("space-y-2") {
        sectionHeader("Persönliche Daten")
        card {
            div("grid grid-cols-1 xl:grid-cols-3 gap-4") {
                val salutationStore = store.map(ContactInformation.salutation() + lensOf(
                    parse = { throw IllegalStateException() },
                    format = { s: Salutation -> s.value }
                ))
                inputText {
                    label("Anrede")
                    value(salutationStore)
                    disabled(true)
                }

                div("xl:col-span-2") {
                    val titleStore = store.map(ContactInformation.title())
                    selectMenu {
                        label("Titel")
                        options = AcademicTitle.all
                        optionsFormat = AcademicTitle::value
                        value(titleStore)
                    }
                }

                val firstNameStore = store.map(ContactInformation.firstName() + Formats.trimString)
                inputText {
                    label("Vorname")
                    value(firstNameStore)
                    disabled(true)
                }

                val namePrefixesStore = store.map(ContactInformation.namePrefix())
                selectMenu {
                    label("Namenszusatz")
                    options = NamePrefix.all
                    optionsFormat = NamePrefix::value
                    value(namePrefixesStore)
                }

                val lastNameStore = store.map(ContactInformation.lastName() + Formats.trimString)
                inputText {
                    label("Nachname")
                    value(lastNameStore)
                    iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.lastname)
                }

                val birthDateStore = store.map(ContactInformation.dateOfBirth())
                val birthDateStoreNullable = storeOf<LocalDate?>(null)
                birthDateStore.data handledBy birthDateStoreNullable.update
                inputDate {
                    label("Geburtsdatum")
                    value(birthDateStoreNullable)
                    disabled(true)
                }
            }
        }
    }

    div("space-y-2") {
        sectionHeader("Anschrift")
        card {
            div("grid grid-cols-1 xl:grid-cols-2 gap-4") {
                val addressStore = store.map(ContactInformation.address())

                val streetStore = addressStore.map(Address.street() + Formats.trimString)
                inputText {
                    label("Straße")
                    value(streetStore)
                    iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.street)
                }

                val streetNumberStore = addressStore.map(Address.houseNumber() + Formats.trimString)
                inputText {
                    label("Hausnummer")
                    value(streetNumberStore)
                    iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.streetNumber)
                }

                val postalCodeStore = addressStore.map(Address.postalCode() + Formats.trimString)
                inputText {
                    label("Postleitzahl")
                    value(postalCodeStore)
                    iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.postalCode)
                }

                val cityStore = addressStore.map(Address.city() + Formats.trimString)
                inputText {
                    label("Ort")
                    value(cityStore)
                    iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.city)
                }
            }
        }
    }

    div("space-y-2") {
        sectionHeader("Kontakt${hyphen}informationen")
        card {
            div("grid grid-cols-1 xl:grid-cols-2 gap-4") {
                val phoneNumberStore = store.map(ContactInformation.phone() + Formats.trimString)
                inputText {
                    label("Festnetz- oder Hauptnummer (optional)")
                    value(phoneNumberStore)
                    iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.phoneNumber)
                }

                val mobileNumberStore = store.map(ContactInformation.mobile() + Formats.trimString)
                inputText {
                    label("Mobil (optional)")
                    value(mobileNumberStore)
                    iconClickableWithPopover(MaterialIconsOutlined.help, HelpPopovers.phoneNumber)
                }

                val mailStore = store.map(ContactInformation.mail() + Formats.trimString)
                inputText {
                    label("Mailadresse für Partnerdaten und Account")
                    value(mailStore)
                }

                val mailConfirmStore = store.map(ContactInformation.mailConfirm() + Formats.trimString)
                store.mailWasEdited.renderIf({ it }) {
                    inputText {
                        label("Neue Mail wiederholen")
                        value(mailConfirmStore)
                    }
                }
                store.mailWasEdited
                    .mapNotNull { wasEdited -> "".takeIf { !wasEdited } }
                    .handledBy(mailConfirmStore.update)
                store.mailWasEdited handledBy store.validateMailRepeat
            }
        }
    }

    div("w-max") {
        btn {
            label("Speichern")
            busy(globalSubmitTracker.data)
            disabled(store.hasInitialData)
        }.clicks.flatMapLatest {
            store.mailWasEdited
        } handledBy store.submitChanges
    }
}

private fun RenderContext.addressCheck(store: ContactInformationStore) {
    messagePageRender(
        header = "Adressvorschlag",
        severity = Severity.Info,
    ) {
        div("flex flex-col gap-8") {
            paragraph {
                +"Zu Ihren Angaben wurde eine alternative Adresse gefunden. Wählen Sie bitte die richtige Adresse aus:"
            }

            combine(
                store.map(ContactInformation.address()).data,
                store.map(ContactInformation.addressSuggestion()).data,
                ::Pair
            ).render { (current, suggestion) ->
                val selectedAddress = storeOf(suggestion)

                radioGroup<Address> {
                    options = listOf(current, suggestion)
                    optionsFormat = { address ->
                        address.run { "$street $houseNumber, $postalCode $city" }
                    }
                    value(selectedAddress)
                }

                div("w-max") {
                    btn {
                        label("Adresse speichern")
                        busy(globalSubmitTracker.data)
                    }.clicks.flatMapLatest {
                        selectedAddress.data
                    } handledBy store.submitAddressChoice
                }
            }
        }
    }
}

private fun RenderContext.successMessage(store: ContactInformationStore) {
    store.current.run {
        messagePagePre(
            header = "Änderungen erfolgreich gespeichert",
            severity = Severity.Success,
            preContent = PortalContactNotes.ContactInformationChanged(
                salutation.value,
                title.value,
                firstName,
                namePrefix.value,
                lastName,
                dateOfBirth.toLocalizedString(),
                address.street,
                address.houseNumber,
                address.postalCode,
                address.city,
                phone,
                mobile,
                mail
            ).note
        )
    }
}